interface StockQuote {
  symbol: string
  price: number
  change: number
  changePercent: number
  previousClose: number
  marketState: 'PRE' | 'REGULAR' | 'POST' | 'CLOSED'
  lastUpdated: Date
}

interface YahooQuote {
  symbol: string
  price: number
  change: number
  changePercent: number
  previousClose: number
  marketState: string
  lastUpdated: string
}

interface StockPriceCache {
  [symbol: string]: {
    data: StockQuote
    timestamp: number
  }
}

class StockPriceService {
  private cache: StockPriceCache = {}
  private cacheTimeout = 60000 // 1 minute cache
  private batchQueue: Set<string> = new Set()
  private batchTimeout: NodeJS.Timeout | null = null
  private batchDelay = 100 // 100ms delay to batch requests

  async getQuote(symbol: string): Promise<StockQuote | null> {
    // Check cache first
    const cached = this.cache[symbol]
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data
    }

    // Add to batch queue
    this.batchQueue.add(symbol)
    
    // Start batch timer if not already running
    if (!this.batchTimeout) {
      this.batchTimeout = setTimeout(() => this.processBatch(), this.batchDelay)
    }

    // Wait for batch to process
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        const result = this.cache[symbol]
        if (result && Date.now() - result.timestamp < this.cacheTimeout) {
          clearInterval(checkInterval)
          resolve(result.data)
        }
      }, 50)

      // Timeout after 5 seconds
      setTimeout(() => {
        clearInterval(checkInterval)
        resolve(null)
      }, 5000)
    })
  }

  async getQuotes(symbols: string[]): Promise<Map<string, StockQuote>> {
    const results = new Map<string, StockQuote>()
    const symbolsToFetch: string[] = []

    // Check cache first
    for (const symbol of symbols) {
      const cached = this.cache[symbol]
      if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
        results.set(symbol, cached.data)
      } else {
        symbolsToFetch.push(symbol)
      }
    }

    // Fetch missing symbols
    if (symbolsToFetch.length > 0) {
      try {
        console.log('Fetching stock prices for:', symbolsToFetch)
        
        // Try multiple approaches
        let data = null
        
        // Primary: Use the stock-prices API (Yahoo v7/v8 direct — most reliable)
        try {
          const apiUrl = `/api/stock-prices?symbols=${symbolsToFetch.join(',')}`
          const response = await fetch(apiUrl)
          if (response.ok) {
            const result = await response.json()
            // Filter out any entries with price=0 or error flag
            const valid: Record<string, YahooQuote> = {}
            for (const [sym, quote] of Object.entries(result as Record<string, YahooQuote & { error?: boolean }>)) {
              if (quote.price > 0 && !quote.error) {
                valid[sym] = quote
              }
            }
            if (Object.keys(valid).length > 0) {
              data = valid
              console.log('Got prices from stock-prices API:', data)
            }
          }
        } catch (e) {
          console.log('stock-prices API failed, trying yahoo-live-prices...')
        }
        
        // Fallback: Try yahoo-finance2 based endpoint
        if (!data) {
          try {
            const response = await fetch(`/api/yahoo-live-prices?symbols=${symbolsToFetch.join(',')}`)
            if (response.ok) {
              const result = await response.json()
              // Filter out error/zero-price entries
              const valid: Record<string, YahooQuote> = {}
              for (const [sym, quote] of Object.entries(result as Record<string, YahooQuote & { error?: boolean }>)) {
                if (quote.price > 0 && !quote.error) {
                  valid[sym] = quote
                }
              }
              if (Object.keys(valid).length > 0) {
                data = valid
                console.log('Got prices from yahoo-live-prices API:', data)
              }
            }
          } catch (e) {
            console.log('yahoo-live-prices API also failed...')
          }
        }
        
        // If API fails, try multiple alternatives
        if (!data) {
          console.log('Trying alternative API methods...')
          
          // Method 1: Try using Yahoo V10 API
          try {
            data = {} as Record<string, YahooQuote>
            for (const symbol of symbolsToFetch) {
              console.log(`Fetching ${symbol} via V10 API...`)
              const yahooV10Url = `https://query2.finance.yahoo.com/v10/finance/quoteSummary/${symbol}?modules=price`
              const proxyUrl = `https://api.allorigins.win/get?url=${encodeURIComponent(yahooV10Url)}`
              
              const response = await fetch(proxyUrl)
              if (response.ok) {
                const proxyData = await response.json()
                try {
                  const yahooData = JSON.parse(proxyData.contents)
                  console.log(`V10 data for ${symbol}:`, yahooData)
                  
                  if (yahooData.quoteSummary && 
                      yahooData.quoteSummary.result && 
                      yahooData.quoteSummary.result[0] && 
                      yahooData.quoteSummary.result[0].price) {
                    const priceData = yahooData.quoteSummary.result[0].price
                    
                    const currentPrice = priceData.regularMarketPrice?.raw || 0
                    const prevClose = priceData.regularMarketPreviousClose?.raw || 0
                    const change = priceData.regularMarketChange?.raw || 0
                    const changePercent = priceData.regularMarketChangePercent?.raw || 0
                    
                    data[symbol] = {
                      symbol: symbol,
                      price: currentPrice,
                      change: change,
                      changePercent: changePercent * 100, // Convert to percentage
                      previousClose: prevClose,
                      marketState: priceData.marketState || 'REGULAR',
                      lastUpdated: new Date().toISOString()
                    }
                    console.log(`Found ${symbol} price via V10: $${currentPrice}`)
                  }
                } catch (e) {
                  console.error(`Failed to parse V10 data for ${symbol}:`, e)
                }
              }
            }
            
            if (Object.keys(data).length > 0) {
              console.log('Successfully fetched prices via V10 API:', data)
            }
          } catch (e) {
            console.error('V10 API failed:', e)
          }
          
          // If all APIs fail, data stays null — component will use static prices from data file
          if (!data || Object.keys(data).length === 0) {
            console.log('All price APIs failed — component will fall back to data file prices')
          }
        }
        
        // Process the data if we got any
        if (data && typeof data === 'object') {
          console.log('Processing data for symbols:', Object.keys(data))
          for (const symbol of Object.keys(data)) {
            const quote = data[symbol]
            const stockQuote: StockQuote = {
              symbol: quote.symbol,
              price: quote.price || 0,
              change: quote.change || 0,
              changePercent: quote.changePercent || 0,
              previousClose: quote.previousClose || 0,
              marketState: this.getMarketState(quote.marketState),
              lastUpdated: new Date(quote.lastUpdated || new Date())
            }
            
            console.log(`Processed ${symbol}:`, stockQuote)

            // Update cache
            this.cache[symbol] = {
              data: stockQuote,
              timestamp: Date.now()
            }

            results.set(symbol, stockQuote)
          }
          console.log('Final results map:', results)
        } else {
          console.log('No data to process')
        }
      } catch (error) {
        console.error('Error fetching stock quotes:', error)
      }
    }
    
    console.log('Returning results map with', results.size, 'items:', results)
    return results
  }

  private async processBatch() {
    const symbols = Array.from(this.batchQueue)
    this.batchQueue.clear()
    this.batchTimeout = null

    if (symbols.length === 0) return

    // Use the same getQuotes method which has fallback logic
    await this.getQuotes(symbols)
  }

  private getMarketState(state: string): 'PRE' | 'REGULAR' | 'POST' | 'CLOSED' {
    switch (state) {
      case 'PRE':
        return 'PRE'
      case 'REGULAR':
        return 'REGULAR'
      case 'POST':
        return 'POST'
      default:
        return 'CLOSED'
    }
  }

  clearCache() {
    this.cache = {}
  }
}

export const stockPriceService = new StockPriceService()
export type { StockQuote }